<template>
  <div class="fs-panel">
    <v-progress-linear v-if="loading" indeterminate color="primary" />

      <!-- ------------------------------------------------------------ cadence -->
      <v-card class="fs-card" outlined>
        <div class="fs-card__head">
          <h2 class="fs-card__title">Cadence</h2>
          <p class="fs-card__desc">
            A user is asked once their account is this many days past their last
            rating. Applies to every company on the platform.
          </p>
        </div>

        <v-form ref="intervalForm" v-model="intervalValid" @submit.prevent="saveInterval">
          <div class="fs-field-row">
            <v-text-field
              v-model.number="intervalDays"
              class="fs-field"
              type="number"
              label="Days between prompts"
              :min="intervalMin"
              :max="intervalMax"
              :rules="intervalRules"
              :disabled="loading || saving"
              :hint="intervalHint"
              persistent-hint
              outlined
              dense />
            <v-btn
              type="submit"
              color="primary"
              depressed
              class="fs-save-btn"
              :loading="saving"
              :disabled="!canSaveInterval">
              Save
            </v-btn>
          </div>
        </v-form>
      </v-card>

      <!-- -------------------------------------------------------------- round -->
      <v-card class="fs-card" outlined>
        <div class="fs-card__head">
          <h2 class="fs-card__title">Feedback round</h2>
          <p class="fs-card__desc">
            Ask <strong>every user on the platform</strong> for a rating on their
            next session, without waiting out the cadence. This also clears
            everyone's&nbsp;&ldquo;remind me in a week&rdquo;, so users who deferred
            will be asked again too.
          </p>
        </div>

        <p v-if="lastRequestedAtLabel" class="fs-meta">
          <v-icon size="16" class="fs-meta__icon">mdi-history</v-icon>
          Last round: {{ lastRequestedAtLabel }}
        </p>
        <p v-else class="fs-meta fs-meta--muted">
          <v-icon size="16" class="fs-meta__icon">mdi-history</v-icon>
          No feedback round has been triggered yet.
        </p>

        <div v-if="lastRound" class="fs-round-result" role="status">
          <v-icon size="18" color="success">mdi-check-circle-outline</v-icon>
          <span>{{ roundResultText }}</span>
        </div>

        <v-btn
          color="primary"
          depressed
          class="fs-round-btn"
          :disabled="loading || triggeringRound"
          @click="confirmOpen = true">
          <v-icon left size="18">mdi-bullhorn-outline</v-icon>
          Request feedback now
      </v-btn>
    </v-card>

    <!-- ------------------------------------------------------- confirmation -->
    <!--
      Persistent by design: this is the one irreversible action on the page. It
      cannot be undone or narrowed to a subset, so it should not be dismissible
      by a stray click outside.
    -->
    <v-dialog v-model="confirmOpen" persistent max-width="440">
      <v-card>
        <v-card-title class="h6 py-3">Ask everyone for feedback?</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-2">
            Every user on the platform will see the feedback prompt the next time
            they start a session.
          </p>
          <p class="mb-0">
            Anyone who chose &ldquo;remind me in a week&rdquo; will be asked again
            as well. This cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="triggeringRound" @click="confirmOpen = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="triggeringRound" @click="triggerRound">
            Request feedback
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { FEEDBACK_INTERVAL_DAYS } from '@Feedback/constants/feedbackConstants';

  export default {
    name: 'FeedbackSettingsPanel',

    data() {
      return {
        intervalDays: null,
        intervalValid: true,
        confirmOpen: false,
      };
    },

    computed: {
      settings() {
        return this.$store.state.FEEDBACK_MANAGEMENT.settings;
      },
      loading() {
        return this.$store.state.FEEDBACK_MANAGEMENT.loading;
      },
      saving() {
        return this.$store.state.FEEDBACK_MANAGEMENT.saving;
      },
      triggeringRound() {
        return this.$store.state.FEEDBACK_MANAGEMENT.triggeringRound;
      },
      lastRound() {
        return this.$store.state.FEEDBACK_MANAGEMENT.lastRound;
      },
      isUsingDefault() {
        return this.$store.getters['FEEDBACK_MANAGEMENT/IS_USING_DEFAULT_INTERVAL'];
      },
      intervalMin() {
        return FEEDBACK_INTERVAL_DAYS.MIN;
      },
      intervalMax() {
        return FEEDBACK_INTERVAL_DAYS.MAX;
      },
      intervalRules() {
        return [
          (v) => (v !== null && v !== '' && v !== undefined) || 'Enter a number of days.',
          (v) => Number.isInteger(Number(v)) || 'Whole days only.',
          (v) =>
            (Number(v) >= this.intervalMin && Number(v) <= this.intervalMax) ||
            `Must be between ${this.intervalMin} and ${this.intervalMax} days.`,
        ];
      },
      /**
       * Says where the current number comes from. "90" chosen by someone and "90"
       * because nobody has chosen anything are different facts, and the admin
       * about to change it should be able to tell them apart.
       */
      intervalHint() {
        if (!this.settings) return '';
        return this.isUsingDefault
          ? `Not set — using the platform default of ${FEEDBACK_INTERVAL_DAYS.DEFAULT} days.`
          : `Currently ${this.settings.intervalDays} days.`;
      },
      canSaveInterval() {
        return (
          this.intervalValid &&
          !this.loading &&
          !this.saving &&
          !!this.settings &&
          Number(this.intervalDays) !== this.settings.intervalDays
        );
      },
      lastRequestedAtLabel() {
        const at = this.settings && this.settings.requestedAt;
        return at ? this.formatInstant(at) : '';
      },
      roundResultText() {
        const cleared = this.lastRound.snoozesCleared;
        const noun = cleared === 1 ? 'deferral' : 'deferrals';
        return cleared > 0
          ? `Everyone will be asked next session. ${cleared} pending ${noun} cleared.`
          : 'Everyone will be asked next session.';
      },
    },

    watch: {
      settings: {
        immediate: true,
        handler(value) {
          if (value) this.intervalDays = value.intervalDays;
        },
      },
    },

    created() {
      this.$store.dispatch('FEEDBACK_MANAGEMENT/LOAD_SETTINGS');
    },

    methods: {
      formatInstant(value) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
      },

      async saveInterval() {
        if (!this.canSaveInterval) return;
        await this.$store.dispatch('FEEDBACK_MANAGEMENT/SAVE_INTERVAL', Number(this.intervalDays));
      },

      async triggerRound() {
        const ok = await this.$store.dispatch('FEEDBACK_MANAGEMENT/TRIGGER_ROUND');
        if (ok) this.confirmOpen = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  // A form reads badly at full page width; the table beside it does not.
  .fs-panel {
    max-width: 720px;
  }

  .fs-card {
    padding: 20px;
    margin-bottom: 16px;
  }

  .fs-card__head {
    margin-bottom: 16px;
  }

  .fs-card__title {
    font-family: var(--ds-font-family-heading, 'Poppins'), sans-serif;
    font-size: 15px;
    font-weight: 600;
    margin: 0;
  }

  .fs-card__desc {
    font-size: 13px;
    line-height: 1.5;
    color: var(--v-text-base);
    margin: 4px 0 0;
    max-width: 60ch;
  }

  .fs-field-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .fs-field {
    max-width: 260px;
  }

  // Aligns the button with the input box rather than the hint line beneath it.
  .fs-save-btn {
    margin-top: 2px;
    height: 40px !important;
    letter-spacing: 0;
    text-transform: none;
  }

  .fs-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    margin: 0 0 12px;
  }

  .fs-meta--muted {
    color: var(--v-text-base);
  }

  .fs-meta__icon {
    opacity: 0.7;
  }

  .fs-round-result {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    background: rgba(44, 194, 149, 0.1);
    border-radius: var(--ds-radius-sm, 4px);
    padding: 8px 10px;
    margin-bottom: 12px;
  }

  .fs-round-btn {
    height: 40px !important;
    letter-spacing: 0;
    text-transform: none;
  }
</style>
